import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ConnectModal } from '../guide/ConnectModal';
import { ConnectButton } from '../guide/ConnectButton';
import { PluggedIcon } from '../guide/PlugIcon';
import { copyText } from '../guide/copyText';
import { KeyDeviceBlock } from './KeyDeviceBlock';
import type { Trial, TrialConfig } from '../../types/trial';
import type { CdnQuota } from '../../types/cdn';
import { activateTrial, unbindTrialDevice } from '../../api/trial.api';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatBytes } from '../../utils/formatBytes';
import { cdnBarPercent, cdnQuotaState } from '../../utils/cdnQuota';
import {
  CDN_EXHAUSTED_NO_TOPUP,
  CDN_TITLE,
  CDN_TRIAL_HINT,
  TRIAL_INVITE_FALLBACK,
  cdnUsageShort,
  trialInviteHint,
} from '../../texts/cdn';
import styles from './TrialCard.module.css';

interface TrialCardProps {
  trial: Trial | null;
  hasSubscription: boolean;
  /** Параметры триала из /trial/config — для текста до активации */
  trialConfig?: TrialConfig | null;
  /** Квота обхода белых списков; показываем только для source === 'trial' */
  cdnQuota?: CdnQuota | null;
  onActivated?: (trial: Trial) => void;
  /** Устройство отвязано: получает триал с device = null для точечного обновления */
  onDeviceUnbound?: (trial: Trial) => void;
}

function formatTimeLeft(expiresAt: Date): { value: string; label: string } {
  const diff = expiresAt.getTime() - Date.now();
  if (diff <= 0) return { value: '0', label: 'осталось' };

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(diff / (1000 * 60 * 60));

  if (days >= 1) {
    return { value: `${days} дн.`, label: 'осталось' };
  }
  return { value: `${hours} ч.`, label: 'осталось' };
}

export function TrialCard({
  trial,
  hasSubscription,
  trialConfig = null,
  cdnQuota = null,
  onActivated,
  onDeviceUnbound,
}: TrialCardProps) {
  const navigate = useNavigate();
  const [activating, setActivating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [autoCopied, setAutoCopied] = useState(false);
  const [unbinding, setUnbinding] = useState(false);

  if (hasSubscription) return null;

  // Приглашение активировать триал
  if (!trial) {
    const handleActivate = async () => {
      setActivating(true);
      try {
        const t = await activateTrial();
        onActivated?.(t);
      } catch (err) {
        toast.error(
          getApiErrorMessage(err, 'Не удалось активировать пробный период. Попробуйте позже')
        );
      } finally {
        setActivating(false);
      }
    };

    return (
      <div className={`${styles.card} ${styles.cardEmpty}`}>
        <div className={styles.header}>
          <span className={styles.title}>Попробуйте бесплатно</span>
          <Badge variant="warning">Пробный период</Badge>
        </div>
        <p className={styles.hint}>
          {trialConfig ? trialInviteHint(trialConfig) : TRIAL_INVITE_FALLBACK}
        </p>
        <div className={styles.actions}>
          <Button onClick={handleActivate} loading={activating} fullWidth>
            Активировать пробный период
          </Button>
        </div>
      </div>
    );
  }

  // Триал завершён
  if (trial.status !== 'active') {
    return (
      <div className={`${styles.card} ${styles.cardExpired}`}>
        <div className={styles.header}>
          <span className={styles.title}>Пробный период</span>
          <Badge variant="error">
            {trial.status === 'expired' ? 'Срок истёк' : 'Трафик исчерпан'}
          </Badge>
        </div>
        <p className={styles.hint}>
          Оформите подписку, чтобы продолжить пользоваться VPN без ограничений.
        </p>
        <div className={styles.actions}>
          <Button onClick={() => navigate('/dashboard/plans')} fullWidth>
            Оформить подписку
          </Button>
        </div>
      </div>
    );
  }

  // Активный триал — паритет с VPNKeyDisplay + трекинг дни/трафик
  const expiresAt = new Date(trial.expiresAt);
  const timeLeft = formatTimeLeft(expiresAt);
  const trafficPct = Math.min(100, trial.trafficPercent);

  // Квота обхода белых списков в триале: отдельный мини-бар, докупки нет
  const trialCdn = cdnQuota && cdnQuota.hasAccess && cdnQuota.source === 'trial' ? cdnQuota : null;
  const trialCdnState = trialCdn ? cdnQuotaState(trialCdn) : 'ok';
  const trialCdnFillClass =
    trialCdnState === 'exhausted'
      ? styles.cdnFillError
      : trialCdnState === 'warning'
        ? styles.cdnFillWarning
        : '';

  const handleUnbind = async () => {
    const confirmed = confirm(
      'Ключ перестанет работать на текущем устройстве. ' +
        'Чтобы подключить другое, вставьте ключ в приложение на нём заново.\n\nОтвязать?',
    );
    if (!confirmed) return;
    setUnbinding(true);
    try {
      await unbindTrialDevice();
      onDeviceUnbound?.({ ...trial, device: null });
      toast.success('Устройство отвязано. Вставьте ключ в приложение на нужном устройстве.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось отвязать устройство. Попробуйте позже'));
    } finally {
      setUnbinding(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trial.subscriptionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = trial.subscriptionUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.titleRow}>
          {trial.device && (
            <span className={styles.boundIcon} title="Ключ привязан к устройству" aria-label="Ключ привязан к устройству">
              <PluggedIcon size={18} />
            </span>
          )}
          <span className={styles.title}>{trial.device?.title || 'Пробный VPN-ключ'}</span>
        </span>
        <Badge variant="warning">Пробный период</Badge>
      </div>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statValue}>{timeLeft.value}</div>
          <div className={styles.statLabel}>{timeLeft.label}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>{formatBytes(trial.trafficUsedBytes)}</div>
          <div className={styles.statLabel}>из {formatBytes(trial.trafficLimitBytes)}</div>
        </div>
      </div>

      <div className={styles.progressBar}>
        <div
          className={`${styles.progressFill} ${trafficPct > 80 ? styles.progressFillWarning : ''}`}
          style={{ width: `${trafficPct}%` }}
        />
      </div>

      {trialCdn && (
        <div className={styles.cdnBlock}>
          <div className={styles.cdnRow}>
            <span className={styles.cdnLabel}>{CDN_TITLE}:</span>
            <span className={`${styles.cdnValue} ${trialCdnState === 'exhausted' ? styles.cdnTextError : ''}`}>
              {cdnUsageShort(trialCdn)}
            </span>
          </div>
          <div
            className={styles.cdnBar}
            role="progressbar"
            aria-label={CDN_TITLE}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(cdnBarPercent(trialCdn))}
          >
            <div
              className={`${styles.cdnFill} ${trialCdnFillClass}`}
              style={{ width: `${cdnBarPercent(trialCdn)}%` }}
            />
          </div>
          <p className={`${styles.cdnHint} ${trialCdnState === 'exhausted' ? styles.cdnTextError : ''}`}>
            {trialCdnState === 'exhausted' ? CDN_EXHAUSTED_NO_TOPUP : CDN_TRIAL_HINT}
          </p>
        </div>
      )}

      <div className={styles.urlContainer}>
        <span className={styles.url}>{trial.subscriptionUrl}</span>
        <button className={styles.copyBtn} onClick={handleCopy}>
          {copied ? 'Скопировано!' : 'Копировать'}
        </button>
      </div>

      <KeyDeviceBlock device={trial.device} />

      <div className={styles.actions}>
        {trial.device ? (
          // Устройство уже подключено: вместо «Подключить» — отвязка
          <ConnectButton connected loading={unbinding} onClick={() => void handleUnbind()} />
        ) : (
          <ConnectButton
            onClick={() => {
              // Копируем в самом обработчике клика: Safari пишет в буфер только внутри жеста
              void copyText(trial.subscriptionUrl).then(setAutoCopied);
              setConnectOpen(true);
            }}
          />
        )}
        <Button size="sm" variant="secondary" onClick={() => navigate('/dashboard/plans')} fullWidth>
          Оформить подписку
        </Button>
      </div>

      <ConnectModal
        isOpen={connectOpen}
        copied={autoCopied}
        onClose={() => setConnectOpen(false)}
        subscriptionUrl={trial.subscriptionUrl}
        title="Подключить пробный ключ"
      />
    </div>
  );
}
